import { useState, useCallback } from 'react';
import Actions from '../../../store/Actions';
import { MappingItem, PriceFetchItem } from '../../../types/generatedTypes';

interface PriceInfo {
  value: number | null;
  loading: boolean;
  error: boolean;
}

export const useInstrumentPrice = () => {
  const [prices, setPrices] = useState<Record<string, PriceInfo>>({});

  const fetchPrice = useCallback(
    async (rowId: string, sourceId: string, sourceData: string) => {
      setPrices(prev => ({
        ...prev,
        [rowId]: { value: null, loading: true, error: false },
      }));

      try {
        const response = await Actions.fetchWizardPrices([
          { source_id: sourceId, source_data: sourceData },
        ]);
        const result = response.prices[0];
        setPrices(prev => ({
          ...prev,
          [rowId]: {
            value: result?.price ?? null,
            loading: false,
            error: result?.price == null,
          },
        }));
      } catch {
        setPrices(prev => ({
          ...prev,
          [rowId]: { value: null, loading: false, error: true },
        }));
      }
    },
    []
  );

  const fetchPricesForItems = useCallback(async (items: MappingItem[]) => {
    // Collect items that have a linked instrument
    const itemsWithSource = items.filter(
      item => item.instrument_source_id?.source_id && item.instrument_source_id?.source_data
    );

    if (itemsWithSource.length === 0) return;

    // Set all to loading
    setPrices(prev => {
      const next = { ...prev };
      for (const item of itemsWithSource) {
        next[item.id] = { value: null, loading: true, error: false };
      }
      return next;
    });

    // Deduplicate by source_id for batch fetch
    const uniqueMap = new Map<string, PriceFetchItem>();
    for (const item of itemsWithSource) {
      const sid = item.instrument_source_id!.source_id!;
      if (!uniqueMap.has(sid)) {
        uniqueMap.set(sid, {
          source_id: sid,
          source_data: item.instrument_source_id!.source_data!,
        });
      }
    }

    try {
      const response = await Actions.fetchWizardPrices(
        Array.from(uniqueMap.values())
      );

      // Build lookup: source_id → price result
      const priceLookup = new Map<string, { price: number | null; error: boolean }>();
      for (const result of response.prices) {
        priceLookup.set(result.source_id, {
          price: result.price ?? null,
          error: result.price == null,
        });
      }

      // Map back to row IDs
      setPrices(prev => {
        const next = { ...prev };
        for (const item of itemsWithSource) {
          const sid = item.instrument_source_id!.source_id!;
          const priceResult = priceLookup.get(sid);
          next[item.id] = {
            value: priceResult?.price ?? null,
            loading: false,
            error: priceResult?.error ?? true,
          };
        }
        return next;
      });
    } catch {
      setPrices(prev => {
        const next = { ...prev };
        for (const item of itemsWithSource) {
          next[item.id] = { value: null, loading: false, error: true };
        }
        return next;
      });
    }
  }, []);

  const clearPrice = useCallback((rowId: string) => {
    setPrices(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  return { prices, fetchPrice, fetchPricesForItems, clearPrice };
};
